/**
 * FO-423 (batch-56) + FO-473 (batch-63) - ÉÉN centrale pauze-resolutie voor
 * werkbon-dagregels, met TWEE onafhankelijke assen.
 *
 * ─── Twee assen, nooit door elkaar ──────────────────────────────────────────
 *
 * Een pauze die van de gefactureerde uren af mag, mag niet vanzelf van de
 * betaalde uren af, en omgekeerd. Daarom legt elke dagregel twee dingen vast:
 *
 *   1. VERLONINGSAS - de feitelijke pauze (`pauzeMinutes`) plus de keuze
 *      `pauzeVerloning`:
 *        - GESCHREVEN  = de pauze gaat van de BETAALDE uren af (dagdienst);
 *        - DOORBETAALD = de pauze telt mee in de betaalde uren (ploegen/nacht).
 *      `WerkbonDayEntry.hours` = de betaalde uren (`betaaldeUren`), en die
 *      voeden weekstaat en verloning.
 *   2. FACTURATIE-AS - `pauzeFacturatieMinuten`: de minuten die van de
 *      FACTURABELE uren afgaan (`facturabeleUren`). Altijd een KLANTREGEL
 *      (FO-511): per dienst, per datum-uitzondering of als klant-standaard op
 *      het werkschema, anders de systeemstandaard (niet bevestigd). Per dagregel
 *      alleen door kantoor te overschrijven, met reden (casus Aludyne: 30
 *      doorbetaald, factuur 30).
 *
 * De oude trede "klant heeft een facturatie-pauze -> arbeidspauze 0" (FO-423
 * trede 6b) is VERVALLEN: de facturatie-as raakt de verloningsas nooit meer.
 *
 * ─── De cascade (per as, per veld) ──────────────────────────────────────────
 *
 * Feitelijke pauze (minuten):
 *   1. expliciet op de dagregel (monteur of kantoor) - wint ALTIJD, ook 0;
 *   2. consignatie - nooit een automatische pauze;
 *   3. per-datum dienst-uitzondering op het werkschema;
 *   4. de werkschema-dienst van die dag (`diensten[].breakMinutes`);
 *   5. werkbon-brede pauze uit het aanmaakformulier (planner-invoer);
 *   6. korte dienst (< 4,5 u) zonder eigen pauze - 0;
 *   7. klant-default (`ClientWorkSchedule.standaardPauzeMinuten`);
 *   8. systeemdefault per diensttype (dag 60 / ploeg en nacht 30,
 *      instelbaar via `UrenInstelling`).
 *
 * Verloning (GESCHREVEN/DOORBETAALD): expliciet op de dagregel -> consignatie
 * (geschreven) -> dienst-uitzondering -> dienst -> klant-default ->
 * systeemdefault per diensttype (dag geschreven / ploeg en nacht doorbetaald).
 *
 * Factuurpauze (minuten) - FO-511 (batch-69): UITSLUITEND de klantregels
 * (`resolveFacturatiePauze`): consignatie (0) -> dienst-uitzondering op datum ->
 * dienst van het werkschema -> klant-standaard -> systeemstandaard per
 * diensttype. De invoer van de medewerker, een handmatige pauze en de
 * verloningskeuze tellen NIET mee: wat de monteur schrijft verandert de factuur
 * nooit. De afspraak-tredes gelden pas vanaf `facturatieAfspraakVanaf`
 * (werkdatum, FO-391-precedent). Alleen een afspraak van een BEVESTIGD
 * werkschema is `bevestigd`; de systeemstandaard nooit. Een afwijking per
 * dagregel kan alleen kantoor zetten, met reden (`pasPauzeAan`).
 *
 * Tot FO-511 viel de factuurpauze zonder eigen afspraak terug op de
 * verloningsas (AFGELEID: geschreven = de pauze, doorbetaald = 0) en kende hij
 * een korte-dienst-trede op de herkomst van de feitelijke pauze. Daar kwam de
 * "geen pauze" op de klantbon vandaan bij een doorbetaalde ploegendienst.
 * `afgeleideFacturatieMinuten` beschrijft nog alleen hoe een dagregel van vóór
 * FO-473 (zonder eigen factuurpauze) gefactureerd werd.
 *
 * "Niet ingevuld" is `null`/`undefined` en valt door naar de volgende trede;
 * `0` is een bewuste keuze ("0 min - geen pauze") en wint. Een lege waarde
 * wordt dus nooit een stille 0: hij krijgt de default met een zichtbare bron.
 *
 * Puur en zonder DB-toegang, zodat élk aanmaak-/boekpad (bulk-create, rotatie-/
 * plangenerator, handmatige aanmaak, monteur-boeking, herrekening) exact
 * dezelfde uitkomst geeft en de regel unit-testbaar is.
 */

/** Ploegensysteem van de werkbon (spiegelt de Prisma `DienstType`-enum). */
export type PauzeDienstType =
  | 'DAGDIENST'
  | 'PLOEGEN_2'
  | 'PLOEGEN_3'
  | 'PLOEGEN_4'
  | 'PLOEGEN_5'
  | 'CONSIGNATIE'

/** Ploegsoort van de dienst (spiegelt de Prisma `PloegSoort`-enum). */
export type PauzePloegSoort = 'DAG' | 'VROEG' | 'LAAT' | 'NACHT'

/**
 * FO-473 - gaat de pauze van de BETAALDE uren af? Spiegelt de Prisma-enum
 * `PauzeVerloning` (DB-type `pauze_verloning`).
 */
export type PauzeVerloning = 'GESCHREVEN' | 'DOORBETAALD'

export const PAUZE_VERLONING_WAARDEN = ['GESCHREVEN', 'DOORBETAALD'] as const satisfies ReadonlyArray<PauzeVerloning>

export const PAUZE_VERLONING_LABELS: Record<PauzeVerloning, string> = {
  GESCHREVEN: 'geschreven (gaat van de betaalde uren af)',
  DOORBETAALD: 'doorbetaald (telt mee in de betaalde uren)',
}

export const PAUZE_VERLONING_KORT: Record<PauzeVerloning, string> = {
  GESCHREVEN: 'geschreven',
  DOORBETAALD: 'doorbetaald',
}

export function isPauzeVerloning(v: unknown): v is PauzeVerloning {
  return v === 'GESCHREVEN' || v === 'DOORBETAALD'
}

/**
 * Herkomst van een pauze-waarde, per as. Spiegelt de Prisma-enum `PauzeBron`
 * (DB-type `pauze_bron`); de drift-test in de api houdt ze gelijk.
 */
export type PauzeBron =
  | 'EXPLICIET'
  | 'MONTEUR'
  | 'CONSIGNATIE'
  | 'DIENST_UITZONDERING'
  | 'DIENST'
  | 'FORMULIER'
  | 'KORTE_DIENST'
  | 'KLANT_DEFAULT'
  | 'SYSTEEM_DEFAULT'
  | 'AFGELEID'
  | 'MIGRATIE'

export const PAUZE_BRON_WAARDEN = [
  'EXPLICIET',
  'MONTEUR',
  'CONSIGNATIE',
  'DIENST_UITZONDERING',
  'DIENST',
  'FORMULIER',
  'KORTE_DIENST',
  'KLANT_DEFAULT',
  'SYSTEEM_DEFAULT',
  'AFGELEID',
  'MIGRATIE',
] as const satisfies ReadonlyArray<PauzeBron>

export const PAUZE_BRON_LABELS: Record<PauzeBron, string> = {
  EXPLICIET: 'handmatig aangepast',
  MONTEUR: 'ingevuld door de monteur',
  CONSIGNATIE: 'consignatie (geen automatische pauze)',
  DIENST_UITZONDERING: 'afwijkende dienst op deze datum',
  DIENST: 'afspraak van de werkschema-dienst',
  FORMULIER: 'ingevuld bij het aanmaken',
  KORTE_DIENST: 'korte dienst (geen pauze)',
  KLANT_DEFAULT: 'standaardafspraak van de klant',
  SYSTEEM_DEFAULT: 'systeemstandaard',
  AFGELEID: 'volgt de verloningsregel',
  MIGRATIE: 'overgenomen van vóór de tweeassige pauze',
}

export function isPauzeBron(v: unknown): v is PauzeBron {
  return typeof v === 'string' && (PAUZE_BRON_WAARDEN as ReadonlyArray<string>).includes(v)
}

export interface PauzeSysteemDefaults {
  /** Dagdienst zonder eigen pauze-afspraak. */
  dagdienstMinuten: number
  /** 2-/3-/4-/5-ploegendienst (en nachtdienst) zonder eigen pauze-afspraak. */
  ploegenMinuten: number
  /** FO-473 - verloning van de dagdienst-pauze zonder eigen afspraak. */
  dagdienstVerloning: PauzeVerloning
  /** FO-473 - verloning van de ploegen-/nachtpauze zonder eigen afspraak. */
  ploegenVerloning: PauzeVerloning
}

/**
 * Systeemdefaults (FO-423 A3 + FO-473 A1): dagdienst 60 geschreven,
 * ploeg/nacht 30 doorbetaald. Alles instelbaar; deze constante is de laatste
 * terugval.
 */
export const PAUZE_SYSTEEM_DEFAULTS: PauzeSysteemDefaults = {
  dagdienstMinuten: 60,
  ploegenMinuten: 30,
  dagdienstVerloning: 'GESCHREVEN',
  ploegenVerloning: 'DOORBETAALD',
}

/**
 * Onder deze dienstduur erft een dienst géén automatische pauze (FO-423 §6.1).
 * 4,5 uur = de grens waarboven pauze in de praktijk altijd is afgesproken; een
 * dienst die expliciet een eigen pauze draagt houdt die uiteraard wél.
 */
export const KORTE_DIENST_DREMPEL_MINUTEN = 270

/** Welke systeemstandaard geldt: dagdienst of ploeg/nacht. */
export type PauzeCategorie = 'DAG' | 'PLOEG'

/**
 * Een nachtdienst valt altijd onder de ploeg/nacht-standaard (spec 173 §A2),
 * ook op een dagdienst-werkbon. Zonder diensttype = dagdienst (dat was ook het
 * bestaande `?? 60`-gedrag).
 */
export function pauzeCategorie(
  dienstType: PauzeDienstType | null | undefined,
  ploegSoort?: PauzePloegSoort | null,
): PauzeCategorie {
  if (ploegSoort === 'NACHT') return 'PLOEG'
  if (dienstType == null || dienstType === 'DAGDIENST' || dienstType === 'CONSIGNATIE') return 'DAG'
  return 'PLOEG'
}

export interface PauzeContext {
  /**
   * Expliciet ingevulde feitelijke pauze (dagregel-invoer, API-body,
   * monteur-boeking). `null`/`undefined` = niet ingevuld; `0` is een geldige
   * bewuste keuze en wint dus óók van elke afleiding.
   */
  expliciet?: number | null
  /**
   * Wie vulde `expliciet` in: de monteur zelf (MONTEUR), de planner in een
   * aanmaakformulier (FORMULIER: geldt voor de hele bon, ook voor dagen die er
   * later bij komen) of kantoor/API voor één dag (EXPLICIET, default).
   */
  explicietDoor?: 'EXPLICIET' | 'MONTEUR' | 'FORMULIER'
  /** Handmatige verloningskeuze op de dagregel (alleen kantoor). */
  explicietVerloning?: PauzeVerloning | null
  // FO-511 (batch-69): er is bewust GEEN `explicietFacturatieMinuten` meer. De
  // factuurpauze komt uitsluitend uit de klantregels hieronder; een afwijking
  // per dagregel zet alleen kantoor, met reden, via `pasPauzeAan`.
  /** Consignatie-werkbon: standby/oproep krijgt nooit automatisch pauze. */
  isConsignatie?: boolean
  /** Pauze uit een per-datum dienst-uitzondering op het klant-werkschema. */
  uitzonderingPauzeMinuten?: number | null
  uitzonderingVerloning?: PauzeVerloning | null
  /** KLANTREGEL: factuurpauze van de dienst-uitzondering (alleen uit het werkschema). */
  uitzonderingFacturatieMinuten?: number | null
  /** De werkschema-dienst die op deze dag geldt. */
  dienstPauzeMinuten?: number | null
  dienstVerloning?: PauzeVerloning | null
  /** KLANTREGEL: factuurpauze van de werkschema-dienst (alleen uit het werkschema). */
  dienstFacturatieMinuten?: number | null
  /**
   * Werkbon-brede pauze die de planner in het aanmaakformulier invulde. Wint van
   * de klant-default en de systeemdefault, maar NIET van een dienst-specifieke
   * afspraak - het werkschema blijft de bron van waarheid per ploeg.
   */
  formulierPauzeMinuten?: number | null
  /** Klant-brede afspraak (`ClientWorkSchedule.standaardPauze*`). */
  klantDefaultPauzeMinuten?: number | null
  klantDefaultVerloning?: PauzeVerloning | null
  /** KLANTREGEL: factuurpauze-standaard van het werkschema. */
  klantDefaultFacturatieMinuten?: number | null
  /**
   * FO-511 - zijn de pauze-afspraken van het werkschema bevestigd
   * (`ClientWorkSchedule.pauzeBevestigdOp`)? Alleen dan is een factuurpauze uit
   * de klantregels "bevestigd" en mag hij zonder voorbehoud naar de klant.
   */
  facturatieKlantregelsBevestigd?: boolean
  /** Diensttype van de werkbon; bepaalt (met de ploegsoort) de systeemdefault. */
  dienstType?: PauzeDienstType | null
  /** Ploegsoort van de dienst; een nachtdienst volgt de ploeg/nacht-standaard. */
  ploegSoort?: PauzePloegSoort | null
  /**
   * Bruto duur van de dienst in minuten (span, incl. nacht-overgang). Nodig
   * voor de korte-dienst-regel en om een afgeleide pauze nooit groter dan de
   * dienst zelf te laten worden.
   */
  dienstduurMinuten?: number | null
  /** Instelbare systeemdefaults; ontbrekende velden vallen terug op de constante. */
  systeemDefaults?: Partial<PauzeSysteemDefaults> | null
  /** Werkdatum van de dagregel (UTC-kalenderdag); nodig voor `facturatieAfspraakVanaf`. */
  datum?: Date | string | null
  /**
   * Vanaf welke werkdatum de factuurpauze-afspraken van het werkschema gelden
   * (`ClientWorkSchedule.facturatiePauzeVanaf`). `null` = altijd.
   */
  facturatieAfspraakVanaf?: Date | string | null
}

/** De verloningsas van een dagregel (`resolveVerloningsPauze`). */
export interface VerloningsPauzeResolutie {
  /** Feitelijke pauze in minuten. */
  minuten: number
  /** Herkomst van de feitelijke pauze. */
  bron: PauzeBron
  /** Gaat de pauze van de betaalde uren af? */
  verloning: PauzeVerloning
  /** Herkomst van de verloningskeuze. */
  verloningBron: PauzeBron
}

export interface PauzeResolutie extends VerloningsPauzeResolutie {
  /** Minuten die van de FACTURABELE uren afgaan. */
  facturatieMinuten: number
  /** Herkomst van de factuurpauze. */
  facturatieBron: PauzeBron
  /**
   * FO-511 - komt de factuurpauze uit een bevestigde klantregel (of de vaste
   * consignatieregel)? Zo niet, dan nooit "geen pauze" richting de klant.
   */
  facturatieBevestigd: boolean
  /** Waarom de factuurpauze (nog) niet bevestigd is; `null` = bevestigd. */
  facturatieOnbevestigdReden: FacturatiePauzeOnbevestigdReden | null
}

// ─── FO-511 (batch-69) - de facturatie-as: uitsluitend de klantregels ───────

/**
 * De klantregels voor de FACTURATIE-as van één dagregel, uit het werkschema van
 * de dagregel (de "locatie": het rooster waaraan de werkbon hangt). Nooit de
 * invoer van de medewerker, nooit de verloningsas.
 */
export interface FacturatieKlantregels {
  /** Factuurpauze van de per-datum dienst-uitzondering. */
  uitzonderingMinuten?: number | null
  /** Factuurpauze van de werkschema-dienst van die dag. */
  dienstMinuten?: number | null
  /** Klant-standaard van het werkschema (`standaardPauzeFacturatieMinuten`). */
  klantDefaultMinuten?: number | null
  /** Zijn de pauze-afspraken van het werkschema bevestigd (`pauzeBevestigdOp`)? */
  bevestigd?: boolean
  /** Ingangsdatum van deze afspraken (`facturatiePauzeVanaf`); `null` = altijd. */
  vanaf?: Date | string | null
}

/** De dienst van de dagregel: consignatie, systeemstandaard en de klem op de dienstduur. */
export interface FacturatieDienst {
  dienstType?: PauzeDienstType | null
  ploegSoort?: PauzePloegSoort | null
  /** Bruto (geplande) duur van de dienst in minuten. */
  dienstduurMinuten?: number | null
  isConsignatie?: boolean
}

/**
 * Waarom een factuurpauze niet bevestigd is:
 *   - GEEN_KLANTREGEL: het werkschema kent voor deze dienst geen factuurpauze
 *     en geen klant-standaard (of de bon hangt aan geen werkschema): de
 *     systeemstandaard staat er als voorstel;
 *   - NIET_BEVESTIGD: er is een afspraak, maar het werkschema is (nog) niet
 *     gecontroleerd en bevestigd;
 *   - VOOR_INGANGSDATUM: de werkdatum ligt vóór de ingangsdatum van de
 *     afspraken van dit werkschema.
 */
export type FacturatiePauzeOnbevestigdReden = 'GEEN_KLANTREGEL' | 'NIET_BEVESTIGD' | 'VOOR_INGANGSDATUM'

export const FACTURATIE_PAUZE_ONBEVESTIGD_LABELS: Record<FacturatiePauzeOnbevestigdReden, string> = {
  GEEN_KLANTREGEL: 'geen pauzeregel van de klant voor deze dienst',
  NIET_BEVESTIGD: 'pauzeregel van de klant nog niet bevestigd',
  VOOR_INGANGSDATUM: 'pauzeregel van de klant geldt pas vanaf een latere datum',
}

export interface FacturatiePauzeResolutie {
  /** Minuten die van de FACTURABELE uren afgaan (kwartier, geklemd). */
  minuten: number
  /** CONSIGNATIE | DIENST_UITZONDERING | DIENST | KLANT_DEFAULT | SYSTEEM_DEFAULT */
  bron: PauzeBron
  /** Mag deze waarde zonder voorbehoud naar de klant (en dus ook "geen pauze")? */
  bevestigd: boolean
  onbevestigdReden: FacturatiePauzeOnbevestigdReden | null
}

/**
 * Bruikbaar pauze-getal? `0` telt mee - dat is een geldige, bewuste keuze.
 * `null`, `undefined`, een lege string, NaN en negatieve waarden tellen NIET:
 * die betekenen "niet ingevuld" en vallen door naar de volgende trede.
 */
export function bruikbaarPauzeGetal(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0
}

const bruikbaar = bruikbaarPauzeGetal

function systeemDefaultVoor(
  categorie: PauzeCategorie,
  defaults: Partial<PauzeSysteemDefaults> | null | undefined,
): { minuten: number; verloning: PauzeVerloning } {
  const minuten = categorie === 'DAG' ? defaults?.dagdienstMinuten : defaults?.ploegenMinuten
  const verloning = categorie === 'DAG' ? defaults?.dagdienstVerloning : defaults?.ploegenVerloning
  const fallback =
    categorie === 'DAG'
      ? { minuten: PAUZE_SYSTEEM_DEFAULTS.dagdienstMinuten, verloning: PAUZE_SYSTEEM_DEFAULTS.dagdienstVerloning }
      : { minuten: PAUZE_SYSTEEM_DEFAULTS.ploegenMinuten, verloning: PAUZE_SYSTEEM_DEFAULTS.ploegenVerloning }
  return {
    minuten: bruikbaar(minuten) ? minuten : fallback.minuten,
    verloning: isPauzeVerloning(verloning) ? verloning : fallback.verloning,
  }
}

/** Datum of `yyyy-MM-dd` -> UTC-dagbegin; `null` bij onbruikbare invoer. */
function utcDagStart(v: Date | string): Date | null {
  const d = typeof v === 'string' ? new Date(v.length === 10 ? `${v}T00:00:00.000Z` : v) : v
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
}

/**
 * Geldt een factuurpauze-afspraak van het werkschema op deze werkdatum?
 * Zonder ingangsdatum: altijd. Met ingangsdatum maar zonder (bruikbare)
 * werkdatum: nee - dan factureren we zoals vóór de afspraak (identiek aan het
 * oude `facturabeleUrenVoorDagEntry`).
 */
export function facturatieAfspraakGeldt(
  datum: Date | string | null | undefined,
  vanaf: Date | string | null | undefined,
): boolean {
  if (vanaf == null) return true
  const grens = utcDagStart(vanaf)
  if (grens == null) return true
  if (datum == null) return false
  const dag = utcDagStart(datum)
  if (dag == null) return false
  return dag.getTime() >= grens.getTime()
}

/**
 * De factuurpauze die uit de verloningsregel volgt (AFGELEID): de pauze bij
 * GESCHREVEN, 0 bij DOORBETAALD.
 *
 * FO-511 (batch-69): NIET meer de terugval van de cascade (de factuurpauze is
 * altijd een klantregel, `resolveFacturatiePauze`). Alleen nog de beschrijving
 * van hoe een dagregel van vóór FO-473 - zonder eigen factuurpauze -
 * gefactureerd werd (`facturabeleUren`, `kopieerPauzeVelden`, het rapport van
 * de herrekening). De drift-guard bewaakt dat de cascade en `pasPauzeAan` hem
 * niet meer gebruiken.
 */
export function afgeleideFacturatieMinuten(
  pauzeMinuten: number,
  verloning: PauzeVerloning | null | undefined,
): number {
  if (!bruikbaar(pauzeMinuten)) return 0
  return (verloning ?? 'GESCHREVEN') === 'DOORBETAALD' ? 0 : pauzeMinuten
}

/** Bovengrens van de feitelijke pauze op een dagregel (DB `chk_pauze_minutes`). */
export const PAUZE_MAX_MINUTEN = 120
/** Bovengrens van de factuurpauze (API; de DB staat tot 360 toe voor de backfill). */
export const FACTUURPAUZE_MAX_MINUTEN = 240

/** Naar beneden op het kwartier (de dagregel-kolommen eisen veelvouden van 15). */
function opKwartier(minuten: number): number {
  return minuten - (minuten % 15)
}

/** Bruikbare (positieve) dienstduur, anders `null`. */
function bruikbareDuur(dienstduurMinuten: number | null | undefined): number | null {
  return bruikbaar(dienstduurMinuten) && dienstduurMinuten > 0 ? dienstduurMinuten : null
}

/**
 * Klemt een afgeleide pauze op de dienstduur (een pauze langer dan de dienst
 * zou negatieve uren opleveren), op de bovengrens en naar beneden op het
 * kwartier: een historische afspraak van bv. 20 min of 135 min mag het aanmaken
 * van een dagregel niet laten vastlopen op de DB-constraint. Naar beneden =
 * nooit meer pauze dan afgesproken.
 */
function klemPauze(minuten: number, max: number, duur: number | null): number {
  return opKwartier(Math.max(0, Math.min(minuten, max, duur ?? minuten)))
}

/**
 * De VERLONINGSAS van een dagregel (FO-423 + FO-473): de feitelijke pauze en of
 * die van de betaalde uren afgaat. Zie de module-docblock voor de cascade.
 *
 * FO-511 (batch-69): ongewijzigd uit `resolvePauze` gehaald - de splitsing raakt
 * de verloning en dus `WerkbonDayEntry.hours` niet (de tests vergelijken hem
 * met de referentie-implementatie van vóór de splitsing). Een EXPLICIETE
 * waarde blijft ongemoeid - die valideert de route (kwartier, max-grenzen) en
 * mag deze pure functie niet stil verbouwen.
 */
export function resolveVerloningsPauze(context: PauzeContext): VerloningsPauzeResolutie {
  const duur = bruikbareDuur(context.dienstduurMinuten)
  const klem = (minuten: number): number => klemPauze(minuten, PAUZE_MAX_MINUTEN, duur)
  const consignatie = context.isConsignatie === true || context.dienstType === 'CONSIGNATIE'
  const korteDienst = duur != null && duur < KORTE_DIENST_DREMPEL_MINUTEN
  const systeem = systeemDefaultVoor(
    pauzeCategorie(context.dienstType, context.ploegSoort),
    context.systeemDefaults,
  )

  // ── Verloningsas, deel 1: de feitelijke pauze ─────────────────────────────
  let minuten: number
  let bron: PauzeBron
  if (bruikbaar(context.expliciet)) {
    // 1. Expliciete invoer wint altijd - ook een bewuste 0 ("doorgewerkt").
    minuten = context.expliciet
    bron = context.explicietDoor ?? 'EXPLICIET'
  } else if (consignatie) {
    // 2. Consignatie: standby-nachten en oproepen krijgen nooit automatisch pauze.
    minuten = 0
    bron = 'CONSIGNATIE'
  } else if (bruikbaar(context.uitzonderingPauzeMinuten)) {
    // 3. Per-datum uitzondering op het werkschema (feestdag-dienst, afwijkende dag).
    minuten = klem(context.uitzonderingPauzeMinuten)
    bron = 'DIENST_UITZONDERING'
  } else if (bruikbaar(context.dienstPauzeMinuten)) {
    // 4. De dienst van die dag uit het klant-werkschema.
    minuten = klem(context.dienstPauzeMinuten)
    bron = 'DIENST'
  } else if (bruikbaar(context.formulierPauzeMinuten)) {
    // 5. Werkbon-brede pauze uit het aanmaakformulier (planner-invoer).
    minuten = klem(context.formulierPauzeMinuten)
    bron = 'FORMULIER'
  } else if (korteDienst) {
    // 6. Korte dienst zonder eigen pauze-afspraak: geen automatische pauze.
    minuten = 0
    bron = 'KORTE_DIENST'
  } else if (bruikbaar(context.klantDefaultPauzeMinuten)) {
    // 7. Klant-brede standaardpauze.
    minuten = klem(context.klantDefaultPauzeMinuten)
    bron = 'KLANT_DEFAULT'
  } else {
    // 8. Systeemdefault per diensttype (instelbaar).
    minuten = klem(systeem.minuten)
    bron = 'SYSTEEM_DEFAULT'
  }

  // ── Verloningsas, deel 2: geschreven of doorbetaald ───────────────────────
  // Elk veld valt los door: een dienst-uitzondering die alleen een tijd zet,
  // laat de verloningskeuze van de dienst staan (spec §6.3).
  let verloning: PauzeVerloning
  let verloningBron: PauzeBron
  if (isPauzeVerloning(context.explicietVerloning)) {
    verloning = context.explicietVerloning
    verloningBron = 'EXPLICIET'
  } else if (consignatie) {
    // Consignatie houdt zijn eigen regels: een (handmatige) pauze op een
    // oproep gaat, zoals altijd, van de uren af.
    verloning = 'GESCHREVEN'
    verloningBron = 'CONSIGNATIE'
  } else if (isPauzeVerloning(context.uitzonderingVerloning)) {
    verloning = context.uitzonderingVerloning
    verloningBron = 'DIENST_UITZONDERING'
  } else if (isPauzeVerloning(context.dienstVerloning)) {
    verloning = context.dienstVerloning
    verloningBron = 'DIENST'
  } else if (isPauzeVerloning(context.klantDefaultVerloning)) {
    verloning = context.klantDefaultVerloning
    verloningBron = 'KLANT_DEFAULT'
  } else {
    verloning = systeem.verloning
    verloningBron = 'SYSTEEM_DEFAULT'
  }

  return { minuten, bron, verloning, verloningBron }
}

/**
 * De FACTURATIE-AS van een dagregel (FO-511, batch-69): uitsluitend de
 * klantregels, nooit de invoer van de medewerker en nooit de verloningsas.
 *
 *   1. consignatie (standby/oproep): 0 - een vaste regel, dus bevestigd;
 *   2. de dienst-uitzondering op die datum;
 *   3. de werkschema-dienst van die dag;
 *   4. de klant-standaard van het werkschema;
 *   5. de systeemstandaard per diensttype (dag 60 / ploeg en nacht 30, de
 *      instelbare pauze-standaard; een korte dienst < 4,5 u: 0). Dat is een
 *      VOORSTEL: altijd onbevestigd, zodat hij nooit als "geen pauze" naar de
 *      klant gaat en de verzend-gate hem tegenhoudt.
 *
 * Tredes 2-4 gelden pas vanaf de ingangsdatum (`klantregels.vanaf`); daarvoor
 * staat de systeemstandaard er, onbevestigd. Ze zijn `bevestigd` zodra het
 * werkschema bevestigd is. Elke waarde wordt geklemd op de dienstduur, op 240
 * min en naar beneden op het kwartier (de kwartierafronding van de dagregel).
 */
export function resolveFacturatiePauze(
  klantregels: FacturatieKlantregels | null | undefined,
  datum: Date | string | null | undefined,
  dienst: FacturatieDienst = {},
  systeemDefaults?: Partial<PauzeSysteemDefaults> | null,
): FacturatiePauzeResolutie {
  const duur = bruikbareDuur(dienst.dienstduurMinuten)
  const klem = (minuten: number): number => klemPauze(minuten, FACTUURPAUZE_MAX_MINUTEN, duur)

  if (dienst.isConsignatie === true || dienst.dienstType === 'CONSIGNATIE') {
    return { minuten: 0, bron: 'CONSIGNATIE', bevestigd: true, onbevestigdReden: null }
  }

  const regels = klantregels ?? {}
  const heeftAfspraak =
    bruikbaar(regels.uitzonderingMinuten) ||
    bruikbaar(regels.dienstMinuten) ||
    bruikbaar(regels.klantDefaultMinuten)
  const geldt = facturatieAfspraakGeldt(datum, regels.vanaf)
  if (geldt) {
    const bevestigd = regels.bevestigd === true
    const reden: FacturatiePauzeOnbevestigdReden | null = bevestigd ? null : 'NIET_BEVESTIGD'
    if (bruikbaar(regels.uitzonderingMinuten)) {
      return { minuten: klem(regels.uitzonderingMinuten), bron: 'DIENST_UITZONDERING', bevestigd, onbevestigdReden: reden }
    }
    if (bruikbaar(regels.dienstMinuten)) {
      return { minuten: klem(regels.dienstMinuten), bron: 'DIENST', bevestigd, onbevestigdReden: reden }
    }
    if (bruikbaar(regels.klantDefaultMinuten)) {
      return { minuten: klem(regels.klantDefaultMinuten), bron: 'KLANT_DEFAULT', bevestigd, onbevestigdReden: reden }
    }
  }

  return {
    minuten: klem(facturatieSysteemVoorstel(dienst, systeemDefaults)),
    bron: 'SYSTEEM_DEFAULT',
    bevestigd: false,
    onbevestigdReden: !geldt && heeftAfspraak ? 'VOOR_INGANGSDATUM' : 'GEEN_KLANTREGEL',
  }
}

/**
 * FO-511 - de systeemstandaard als VOORSTEL voor de factuurpauze van een dienst
 * zonder klantregel: de pauze-standaard per diensttype (dag 60 / ploeg en nacht
 * 30, instelbaar), en 0 voor een korte dienst (< 4,5 u). Ongeklemd; de klem op
 * de dienstduur en het kwartier doet de aanroeper.
 */
export function facturatieSysteemVoorstel(
  dienst: FacturatieDienst,
  systeemDefaults?: Partial<PauzeSysteemDefaults> | null,
): number {
  const duur = bruikbareDuur(dienst.dienstduurMinuten)
  if (duur != null && duur < KORTE_DIENST_DREMPEL_MINUTEN) return 0
  return systeemDefaultVoor(pauzeCategorie(dienst.dienstType, dienst.ploegSoort), systeemDefaults).minuten
}

/** De klantregels uit een (platte) `PauzeContext`. */
export function facturatieKlantregelsVan(context: PauzeContext): FacturatieKlantregels {
  return {
    uitzonderingMinuten: context.uitzonderingFacturatieMinuten ?? null,
    dienstMinuten: context.dienstFacturatieMinuten ?? null,
    klantDefaultMinuten: context.klantDefaultFacturatieMinuten ?? null,
    bevestigd: context.facturatieKlantregelsBevestigd === true,
    vanaf: context.facturatieAfspraakVanaf ?? null,
  }
}

/**
 * DE pauze-regel: beide assen van één dagregel. De verloningsas komt uit
 * `resolveVerloningsPauze`, de facturatie-as uit `resolveFacturatiePauze` met
 * alleen de klantregels uit de context - de verloningsas gaat er bewust niet in.
 */
export function resolvePauze(context: PauzeContext): PauzeResolutie {
  const verloningsas = resolveVerloningsPauze(context)
  const facturatieas = resolveFacturatiePauze(
    facturatieKlantregelsVan(context),
    context.datum,
    {
      dienstType: context.dienstType,
      ploegSoort: context.ploegSoort,
      dienstduurMinuten: context.dienstduurMinuten,
      isConsignatie: context.isConsignatie,
    },
    context.systeemDefaults,
  )
  return {
    ...verloningsas,
    facturatieMinuten: facturatieas.minuten,
    facturatieBron: facturatieas.bron,
    facturatieBevestigd: facturatieas.bevestigd,
    facturatieOnbevestigdReden: facturatieas.onbevestigdReden,
  }
}

/** Mag de factuurpauze groter zijn dan de feitelijke pauze? Ja, maar met melding (§6.1). */
export function factuurpauzeBovenPauze(r: {
  minuten: number
  facturatieMinuten: number
}): boolean {
  return r.facturatieMinuten > r.minuten
}

// ─── Dagregel-velden (schrijfvorm) ──────────────────────────────────────────

/**
 * De pauze-kolommen van `WerkbonDayEntry`, precies zoals een aanmaak- of
 * boekpad ze wegschrijft. Eén vorm voor elk pad: spread hem in de create-data.
 */
export interface DagregelPauzeVelden {
  pauzeMinutes: number
  pauzeVerloning: PauzeVerloning
  pauzeFacturatieMinuten: number
  pauzeBron: PauzeBron
  pauzeVerloningBron: PauzeBron
  pauzeFacturatieBron: PauzeBron
  /** Verloningsas bewust gekozen: de herrekening laat hem staan. */
  pauzeHandmatig: boolean
  /** Facturatie-as bewust gekozen: de herrekening laat hem staan. */
  pauzeHandmatigFacturatie: boolean
  /**
   * FO-511 - waarom kantoor de factuurpauze van deze dagregel afwijkend van de
   * klantregel zette. Staat op de interne én de klantbon ("pauze afwijkend").
   * `null` zolang de factuurpauze de klantregel volgt.
   */
  pauzeFacturatieReden: string | null
}

const HANDMATIGE_PAUZE_BRONNEN: ReadonlySet<PauzeBron> = new Set<PauzeBron>([
  'EXPLICIET',
  'MONTEUR',
  'FORMULIER',
])

/** Resolutie -> dagregel-kolommen (inclusief de handmatig-vlag per as). */
export function dagregelPauzeVelden(r: PauzeResolutie): DagregelPauzeVelden {
  return {
    pauzeMinutes: r.minuten,
    pauzeVerloning: r.verloning,
    pauzeFacturatieMinuten: r.facturatieMinuten,
    pauzeBron: r.bron,
    pauzeVerloningBron: r.verloningBron,
    pauzeFacturatieBron: r.facturatieBron,
    pauzeHandmatig: HANDMATIGE_PAUZE_BRONNEN.has(r.bron) || r.verloningBron === 'EXPLICIET',
    // FO-511 - de cascade zet nooit een handmatige factuurpauze: die komt alleen
    // van kantoor, met reden (`pasPauzeAan`).
    pauzeHandmatigFacturatie: false,
    pauzeFacturatieReden: null,
  }
}

/**
 * Consignatie (standby/oproep): 0 op beide assen, met herkomst CONSIGNATIE.
 * Eén bron voor elk pad dat consignatie-dagregels maakt.
 */
export function consignatiePauzeVelden(): DagregelPauzeVelden {
  return dagregelPauzeVelden(resolvePauze({ isConsignatie: true }))
}

/**
 * Een blok op eigen tijden zonder rooster (garage, kantoor-zelfbon, cursusdag):
 * een vaste pauze als bewuste keuze, met herkomst - nooit een stille waarde.
 * `handmatig` staat standaard aan (de herrekening laat het blok staan).
 * `door`: `EXPLICIET` (kantoor/API) of `FORMULIER` (ingevuld bij het aanmaken,
 * bv. een gegenereerde cursusdag).
 *
 * FO-511 - zo'n blok heeft geen klant en geen rooster, dus ook geen klantregel:
 * de vaste pauze geldt op beide assen, met dezelfde herkomst. Hij gaat nooit
 * naar een klant; doet hij dat toch, dan houdt de verzend-gate hem tegen (een
 * vaste pauze is geen bevestigde klantregel).
 */
export function vastePauzeVelden(
  minuten: number,
  handmatig?: boolean,
  door: 'EXPLICIET' | 'FORMULIER' = 'EXPLICIET',
): DagregelPauzeVelden {
  const verloningsas = resolveVerloningsPauze({
    expliciet: minuten,
    explicietDoor: door,
    dienstType: 'DAGDIENST',
  })
  return {
    pauzeMinutes: verloningsas.minuten,
    pauzeVerloning: verloningsas.verloning,
    pauzeFacturatieMinuten: verloningsas.minuten,
    pauzeBron: verloningsas.bron,
    pauzeVerloningBron: verloningsas.verloningBron,
    pauzeFacturatieBron: door,
    pauzeHandmatig: handmatig ?? HANDMATIGE_PAUZE_BRONNEN.has(verloningsas.bron),
    pauzeHandmatigFacturatie: false,
    pauzeFacturatieReden: null,
  }
}

/**
 * Een intern blok op eigen tijden zonder rooster (garage, kantoor-zelfbon):
 * een bewuste "0 min - geen pauze" met herkomst, nooit een stille 0.
 */
export function geenPauzeVelden(): DagregelPauzeVelden {
  return vastePauzeVelden(0)
}

/**
 * De opgeslagen pauze-stand van een dagregel. Kolommen van vóór FO-473 of van
 * een pad dat ze (nog) niet vult, zijn `null`: verloning gedraagt zich dan als
 * GESCHREVEN (het historische gedrag) en de factuurpauze volgt de verloning.
 */
export interface DagregelPauzeStand {
  pauzeMinutes: number
  pauzeVerloning: PauzeVerloning | null
  pauzeFacturatieMinuten: number | null
  pauzeBron: PauzeBron | null
  pauzeVerloningBron: PauzeBron | null
  pauzeFacturatieBron: PauzeBron | null
  pauzeHandmatig: boolean
  pauzeHandmatigFacturatie: boolean
  /** FO-511 - reden van een handmatige factuurpauze (optioneel: oude lezers kennen hem niet). */
  pauzeFacturatieReden?: string | null
}

export interface DagregelPauzeWijziging {
  /** Nieuwe feitelijke pauze (monteur of kantoor). */
  pauzeMinutes?: number
  /** Nieuwe verloningskeuze (alleen kantoor). */
  pauzeVerloning?: PauzeVerloning
  /** Nieuwe factuurpauze (alleen kantoor). */
  pauzeFacturatieMinuten?: number
  /** FO-511 - de reden bij een nieuwe factuurpauze (komt op de werkbon). */
  pauzeFacturatieReden?: string
  /** Wie wijzigt de feitelijke pauze: de monteur zelf of kantoor. */
  door: 'MONTEUR' | 'EXPLICIET'
}

/**
 * Past een handmatige wijziging toe op één as van een dagregel (spec §B7):
 *
 *   - alleen een ECHT gewijzigde waarde telt (portal en planbord sturen de
 *     pauze altijd mee, ook onaangeraakt);
 *   - de gewijzigde as krijgt de handmatig-vlag, de andere as niet;
 *   - FO-511 (batch-69): de factuurpauze is een klantregel. Een wijziging van de
 *     pauze of de verloning - door de monteur of door kantoor - verandert hem
 *     NOOIT. Alleen kantoor zet hem bewust (`pauzeFacturatieMinuten`), en dan
 *     met een reden die op de werkbon komt (`pauzeFacturatieReden`).
 *
 * Een dagregel van vóór FO-473 zonder eigen factuurpauze (`null`) houdt de
 * factuurpauze waarmee hij tot nu toe gefactureerd werd: de afgeleide waarde
 * van de OUDE stand. Zo raakt ook daar de nieuwe invoer de factuur niet.
 */
export function pasPauzeAan(
  stand: DagregelPauzeStand,
  w: DagregelPauzeWijziging,
): DagregelPauzeVelden {
  const huidigeVerloning: PauzeVerloning = stand.pauzeVerloning ?? 'GESCHREVEN'
  const minutenGewijzigd = w.pauzeMinutes !== undefined && w.pauzeMinutes !== stand.pauzeMinutes
  const verloningGewijzigd = w.pauzeVerloning !== undefined && w.pauzeVerloning !== huidigeVerloning
  // Delta-review MIDDEL-2 - een GELIJKE factuurpauze mét reden is ook een bewuste keuze
  // ("zo vastleggen", bijvoorbeeld zoals de klant de uren accordeerde): dan wordt hij
  // handmatig, de herrekening laat hem staan en de klantpauze geldt (met de reden op
  // de bon). Zonder reden blijft een gelijke waarde geen wijziging.
  const factuurVastgelegd =
    w.pauzeFacturatieMinuten !== undefined &&
    w.pauzeFacturatieMinuten === stand.pauzeFacturatieMinuten &&
    !stand.pauzeHandmatigFacturatie &&
    (w.pauzeFacturatieReden?.trim() ?? '') !== ''
  const factuurGewijzigd =
    (w.pauzeFacturatieMinuten !== undefined && w.pauzeFacturatieMinuten !== stand.pauzeFacturatieMinuten) ||
    factuurVastgelegd

  const pauzeMinutes = minutenGewijzigd ? (w.pauzeMinutes as number) : stand.pauzeMinutes
  const pauzeVerloning = verloningGewijzigd ? (w.pauzeVerloning as PauzeVerloning) : huidigeVerloning

  let pauzeFacturatieMinuten: number
  let pauzeFacturatieBron: PauzeBron
  let pauzeHandmatigFacturatie = stand.pauzeHandmatigFacturatie
  let pauzeFacturatieReden = stand.pauzeFacturatieReden ?? null
  if (factuurGewijzigd) {
    pauzeFacturatieMinuten = w.pauzeFacturatieMinuten as number
    pauzeFacturatieBron = 'EXPLICIET'
    pauzeHandmatigFacturatie = true
    pauzeFacturatieReden = w.pauzeFacturatieReden?.trim() || pauzeFacturatieReden
  } else {
    pauzeFacturatieMinuten =
      stand.pauzeFacturatieMinuten ?? afgeleideFacturatieMinuten(stand.pauzeMinutes, huidigeVerloning)
    pauzeFacturatieBron = stand.pauzeFacturatieBron ?? 'MIGRATIE'
  }

  return {
    pauzeMinutes,
    pauzeVerloning,
    pauzeFacturatieMinuten,
    pauzeBron: minutenGewijzigd ? w.door : (stand.pauzeBron ?? 'MIGRATIE'),
    pauzeVerloningBron: verloningGewijzigd ? 'EXPLICIET' : (stand.pauzeVerloningBron ?? 'MIGRATIE'),
    pauzeFacturatieBron,
    pauzeHandmatig: stand.pauzeHandmatig || minutenGewijzigd || verloningGewijzigd,
    pauzeHandmatigFacturatie,
    pauzeFacturatieReden,
  }
}

/**
 * FO-473 (review batch-63) - minuten van het vangnet (de oude schema-brede
 * facturatie-pauze) voor een dagregel zonder eigen factuurpauze: dezelfde
 * voorwaarden als `facturabeleUren` (waarde > 0, uren > 0, werkdatum op of na
 * de ingangsdatum). 0 = geen vangnet.
 */
export function vangnetFactuurpauzeMinuten(
  oude: { minuten: number; vanaf: Date | string | null } | null | undefined,
  datum: Date | string | null | undefined,
  hours: number,
): number {
  if (!oude || !(oude.minuten > 0) || !(hours > 0)) return 0
  return facturatieAfspraakGeldt(datum, oude.vanaf) ? oude.minuten : 0
}

/**
 * FO-473 (review batch-63) - de stand van een dagregel ZONDER eigen
 * factuurpauze zoals de backfill (`docs/specs/173-na-deploy-backfill.sql`) hem
 * vastlegt: herkomst migratie, factuurpauze = de afgeleide pauze plus het
 * vangnet (bron klant-default). Zo raakt een schrijfactie op zo'n regel (door
 * de vorige revisie in het deploy-venster aangemaakt) het vangnet niet kwijt.
 * Een stand mét eigen factuurpauze, of zonder vangnet, blijft ongewijzigd.
 */
export function metFactuurpauzeVangnet(
  stand: DagregelPauzeStand,
  vangnetMinuten: number,
): DagregelPauzeStand {
  if (stand.pauzeFacturatieMinuten != null || !(vangnetMinuten > 0)) return stand
  return {
    ...stand,
    pauzeBron: stand.pauzeBron ?? 'MIGRATIE',
    pauzeVerloningBron: stand.pauzeVerloningBron ?? 'MIGRATIE',
    pauzeFacturatieMinuten:
      afgeleideFacturatieMinuten(stand.pauzeMinutes, stand.pauzeVerloning) + vangnetMinuten,
    pauzeFacturatieBron: 'KLANT_DEFAULT',
  }
}

// ─── FO-511 (batch-69) - de klantpauze van een dagregel beoordelen ──────────

/**
 * De factuurpauze waarmee een dagregel NU gefactureerd wordt: de eigen waarde,
 * of (dagregel van vóór FO-473 zonder eigen factuurpauze) de afgeleide pauze
 * plus het vangnet van de oude schema-brede facturatie-pauze - precies wat
 * `facturabeleUren` rekent.
 */
export function effectieveFactuurpauzeMinuten(input: {
  stand: Pick<DagregelPauzeStand, 'pauzeMinutes' | 'pauzeVerloning' | 'pauzeFacturatieMinuten'>
  hours: number
  datum: Date | string | null | undefined
  oudeFacturatiePauze?: { minuten: number; vanaf: Date | string | null } | null
}): number {
  const { stand } = input
  if (stand.pauzeFacturatieMinuten != null) return stand.pauzeFacturatieMinuten
  return (
    afgeleideFacturatieMinuten(stand.pauzeMinutes, stand.pauzeVerloning) +
    vangnetFactuurpauzeMinuten(input.oudeFacturatiePauze, input.datum, input.hours)
  )
}

/** Waarom de factuurpauze van een dag (nog) niet naar de klant mag (spiegelt `KlantpauzeProbleemKey`). */
export type FactuurpauzeDagProbleem =
  | FacturatiePauzeOnbevestigdReden
  | 'AFWIJKEND'
  | 'WERKSCHEMA_VERWIJDERD'

export interface FactuurpauzeDagInput {
  stand: DagregelPauzeStand
  /** Opgeslagen betaalde uren; een dag zonder uren gaat niet op de factuur. */
  hours: number
  datum: Date | string
  /** De klantregel van deze dag (de facturatie-as van de cascade). */
  regel: Pick<PauzeResolutie, 'facturatieMinuten' | 'facturatieBevestigd' | 'facturatieOnbevestigdReden'>
  oudeFacturatiePauze?: { minuten: number; vanaf: Date | string | null } | null
  /** Het werkschema van de bon is weg en er is geen eenduidige opvolger. */
  werkschemaVerwijderd?: boolean
}

/**
 * FO-511 §C6 - mag de factuurpauze van deze dagregel zonder voorbehoud naar de
 * klant of Finqle? `null` = ja; anders de reden waarom niet:
 *
 *   - een dag zonder uren, of een factuurpauze die kantoor met reden zette
 *     (`pauzeHandmatigFacturatie`): altijd goed;
 *   - geen eenduidig werkschema meer: WERKSCHEMA_VERWIJDERD;
 *   - de klantregel is niet bevestigd (of alleen het voorstel van de
 *     systeemstandaard): de reden van de klantregel;
 *   - bevestigd, maar de dagregel draagt een andere factuurpauze (aangemaakt of
 *     herrekend vóór de bevestiging, of onder de oude regel): AFWIJKEND.
 */
export function beoordeelFactuurpauzeDag(input: FactuurpauzeDagInput): FactuurpauzeDagProbleem | null {
  if (!(Number(input.hours) > 0)) return null
  if (input.stand.pauzeHandmatigFacturatie) return null
  if (input.werkschemaVerwijderd) return 'WERKSCHEMA_VERWIJDERD'
  if (!input.regel.facturatieBevestigd) return input.regel.facturatieOnbevestigdReden ?? 'NIET_BEVESTIGD'
  const effectief = effectieveFactuurpauzeMinuten({
    stand: input.stand,
    hours: input.hours,
    datum: input.datum,
    oudeFacturatiePauze: input.oudeFacturatiePauze,
  })
  return effectief === input.regel.facturatieMinuten ? null : 'AFWIJKEND'
}

const DAG_PROBLEEM_LABELS: Record<FactuurpauzeDagProbleem, string> = {
  ...FACTURATIE_PAUZE_ONBEVESTIGD_LABELS,
  AFWIJKEND: 'factuurpauze wijkt af van de bevestigde pauzeregel (nog niet herrekend)',
  WERKSCHEMA_VERWIJDERD: 'het werkschema van deze werkbon is verwijderd',
}

/**
 * Leesbare uitleg bij de dagen zonder geldende klantpauze, gegroepeerd per
 * reden: "Op 3 dagen is de pauze op de factuur geen geldende pauzeregel van de
 * klant: geen pauzeregel van de klant voor deze dienst (2), ...". Leeg zonder
 * dagen.
 */
export function klantpauzeSamenvatting(dagen: ReadonlyArray<{ probleem: FactuurpauzeDagProbleem }>): string {
  if (dagen.length === 0) return ''
  const perReden = new Map<FactuurpauzeDagProbleem, number>()
  for (const d of dagen) perReden.set(d.probleem, (perReden.get(d.probleem) ?? 0) + 1)
  const delen = [...perReden.entries()].map(([reden, n]) => `${DAG_PROBLEEM_LABELS[reden]} (${n})`)
  const aantal = dagen.length === 1 ? 'Op 1 dag' : `Op ${dagen.length} dagen`
  return `${aantal} is de pauze op de factuur geen geldende pauzeregel van de klant: ${delen.join(', ')}.`
}

const UREN_NL = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 })

/**
 * Integrator-review H3 (FO-511) - welke factuurpauze er zonder pauzeregel van de
 * klant is aangenomen, en wat er uit de pauze van de monteur volgde (de stand van
 * vóór FO-511). Een nieuwe dagregel zonder regel factureert meteen met de
 * systeemstandaard (60 minuten voor een dagdienst, 30 voor een ploeg); waar de
 * monteur 30 minuten schreef is dat 0,5 uur minder factuur. Dat mag niet stil
 * gebeuren: de waarschuwing en het signaal noemen het. Alleen dagen met de
 * systeemstandaard (delta-review LAAG-6: een waarde uit het werkschema of de
 * backfill is geen aanname van het systeem) zonder bevestigde regel, waarop de twee
 * verschillen; leeg als er niets is aangenomen.
 */
export function klantpauzeAannameZin(
  dagen: ReadonlyArray<{
    probleem: FactuurpauzeDagProbleem
    factuurpauzeMinuten: number
    factuurpauzeBron: PauzeBron | null
    afgeleideFactuurpauzeMinuten: number
  }>,
): string {
  const groepen = new Map<string, { aangenomen: number; afgeleid: number; aantal: number }>()
  for (const d of dagen) {
    // Een bevestigde regel (AFWIJKEND) of een verwijderd werkschema: niets aangenomen.
    if (d.probleem === 'AFWIJKEND' || d.probleem === 'WERKSCHEMA_VERWIJDERD') continue
    if (d.factuurpauzeBron !== 'SYSTEEM_DEFAULT') continue
    if (d.factuurpauzeMinuten === d.afgeleideFactuurpauzeMinuten) continue
    const sleutel = `${d.factuurpauzeMinuten}|${d.afgeleideFactuurpauzeMinuten}`
    const groep = groepen.get(sleutel) ?? {
      aangenomen: d.factuurpauzeMinuten,
      afgeleid: d.afgeleideFactuurpauzeMinuten,
      aantal: 0,
    }
    groep.aantal++
    groepen.set(sleutel, groep)
  }
  if (groepen.size === 0) return ''
  const delen = [...groepen.values()].map((g) => {
    const verschil = UREN_NL.format(Math.abs(g.aangenomen - g.afgeleid) / 60)
    const richting = g.aangenomen > g.afgeleid ? 'minder' : 'meer'
    const dagenTekst = g.aantal === 1 ? '1 dag' : `${g.aantal} dagen`
    return (
      `${g.aangenomen} min in plaats van ${g.afgeleid} min uit de pauze van de monteur ` +
      `(${dagenTekst}; ${verschil} uur ${richting} op de factuur per dag)`
    )
  })
  return `Zonder pauzeregel van de klant factureert de systeemstandaard: ${delen.join('; ')}.`
}

/** De factuurpauze volgens de klantregel voor een kopie (zie `kopieerPauzeVelden`). */
export interface FactuurKlantregel {
  minuten: number
  bron: PauzeBron
}

/**
 * De pauze-stand van een bestaande dagregel als schrijfvorm voor een KOPIE
 * (extra inzet, duplicaat): dezelfde planning, dus dezelfde VERLONINGSAS -
 * inclusief de handmatig-vlag. Kolommen van vóór FO-473 krijgen een expliciete
 * herkomst i.p.v. `null`.
 *
 * FO-511 (review H2) - de FACTUUR-as wordt niet blind gekopieerd: een kopie van
 * een bon van vóór FO-511 zou anders zijn afgeleide 0 ("geen pauze") op een
 * verse dagregel overnemen. Een handmatige factuurpauze (kantoor, met reden)
 * gaat mee mét de reden; anders geldt `factuurKlantregel` (de klantregel van
 * de dag, uit `resolveDagregelPauze`). Alleen als de aanroeper die niet kan
 * bepalen, blijft de waarde van de bron staan - de klantpauze-beoordeling, de
 * verzend-gate en de herrekening vangen een afwijking dan op.
 */
export function kopieerPauzeVelden(
  stand: DagregelPauzeStand,
  factuurKlantregel?: FactuurKlantregel | null,
): DagregelPauzeVelden {
  const verloning = stand.pauzeVerloning ?? 'GESCHREVEN'
  const verloningsas = {
    pauzeMinutes: stand.pauzeMinutes,
    pauzeVerloning: verloning,
    pauzeBron: stand.pauzeBron ?? 'MIGRATIE',
    pauzeVerloningBron: stand.pauzeVerloningBron ?? 'MIGRATIE',
    pauzeHandmatig: stand.pauzeHandmatig,
  }
  if (stand.pauzeHandmatigFacturatie && stand.pauzeFacturatieMinuten != null) {
    return {
      ...verloningsas,
      pauzeFacturatieMinuten: stand.pauzeFacturatieMinuten,
      pauzeFacturatieBron: stand.pauzeFacturatieBron ?? 'EXPLICIET',
      pauzeHandmatigFacturatie: true,
      // Een kopie van een handmatige factuurpauze houdt zijn reden (die staat op de bon).
      pauzeFacturatieReden: stand.pauzeFacturatieReden ?? null,
    }
  }
  if (factuurKlantregel) {
    return {
      ...verloningsas,
      pauzeFacturatieMinuten: factuurKlantregel.minuten,
      pauzeFacturatieBron: factuurKlantregel.bron,
      pauzeHandmatigFacturatie: false,
      pauzeFacturatieReden: null,
    }
  }
  return {
    ...verloningsas,
    pauzeFacturatieMinuten:
      stand.pauzeFacturatieMinuten ?? afgeleideFacturatieMinuten(stand.pauzeMinutes, verloning),
    pauzeFacturatieBron: stand.pauzeFacturatieBron ?? 'AFGELEID',
    pauzeHandmatigFacturatie: false,
    pauzeFacturatieReden: null,
  }
}

/** Heeft een wijziging iets aan de pauze-kolommen veranderd? */
export function pauzeVeldenGewijzigd(stand: DagregelPauzeStand, nieuw: DagregelPauzeVelden): boolean {
  return (
    stand.pauzeMinutes !== nieuw.pauzeMinutes ||
    (stand.pauzeVerloning ?? 'GESCHREVEN') !== nieuw.pauzeVerloning ||
    stand.pauzeFacturatieMinuten !== nieuw.pauzeFacturatieMinuten ||
    stand.pauzeBron !== nieuw.pauzeBron ||
    stand.pauzeVerloningBron !== nieuw.pauzeVerloningBron ||
    stand.pauzeFacturatieBron !== nieuw.pauzeFacturatieBron ||
    stand.pauzeHandmatig !== nieuw.pauzeHandmatig ||
    stand.pauzeHandmatigFacturatie !== nieuw.pauzeHandmatigFacturatie ||
    (stand.pauzeFacturatieReden ?? null) !== nieuw.pauzeFacturatieReden
  )
}

// ─── Werkschema-afspraak lezen ──────────────────────────────────────────────

/** De pauze-afspraak van één werkschema-dienst of dienst-uitzondering. */
export interface PauzeAfspraak {
  /** `breakMinutes`; `null` = niet ingevuld (de cascade vult 'm). */
  minuten: number | null
  verloning: PauzeVerloning | null
  facturatieMinuten: number | null
}

export const LEGE_PAUZE_AFSPRAAK: PauzeAfspraak = {
  minuten: null,
  verloning: null,
  facturatieMinuten: null,
}

/**
 * Leest de afspraak uit een ruwe dienst-/uitzondering-JSON
 * (`{ breakMinutes, pauzeVerloning, pauzeFacturatieMinuten }`). Tolerant: een
 * onbruikbare waarde telt als "niet ingevuld", nooit als 0.
 */
export function leesPauzeAfspraak(raw: unknown): PauzeAfspraak {
  if (!raw || typeof raw !== 'object') return LEGE_PAUZE_AFSPRAAK
  const d = raw as Record<string, unknown>
  return {
    minuten: bruikbaar(d.breakMinutes) ? d.breakMinutes : null,
    verloning: isPauzeVerloning(d.pauzeVerloning) ? d.pauzeVerloning : null,
    facturatieMinuten: bruikbaar(d.pauzeFacturatieMinuten) ? d.pauzeFacturatieMinuten : null,
  }
}

/** Eén inline veldfout bij het bevestigen van een werkschema (FO-511 §B4). */
export interface FactuurpauzeBevestigVeldfout {
  /** `diensten.<i>.pauzeFacturatieMinuten` of `standaardPauzeFacturatieMinuten`. */
  field: string
  message: string
}

/** De samenvatting bij een geweigerde bevestiging (API-melding en formulier). */
export const FACTUURPAUZE_BEVESTIG_MELDING =
  'Bevestigen kan pas als elke dienst een factuurpauze heeft (of de klant een standaard factuurpauze). ' +
  'Een pauze op de factuur is een afspraak met de klant en wordt nooit aangenomen.'

/**
 * FO-511 (batch-69) §B4 - de factuurpauze is per dienst verplicht voordat een
 * werkschema bevestigd kan worden (0 is een geldige, bewuste keuze). Een
 * klant-standaard factuurpauze dekt de diensten zonder eigen waarde. Eén regel
 * voor de API (de 422) en het formulier (de inline veldfout vóór het opslaan).
 * Leeg = bevestigen kan.
 */
export function factuurpauzeBevestigVeldfouten(
  diensten: unknown,
  standaardPauzeFacturatieMinuten: number | null | undefined,
): FactuurpauzeBevestigVeldfout[] {
  if (bruikbaar(standaardPauzeFacturatieMinuten)) return []
  const lijst = Array.isArray(diensten) ? (diensten as unknown[]) : []
  if (lijst.length === 0) {
    return [
      {
        field: 'standaardPauzeFacturatieMinuten',
        message: 'Dit werkschema heeft geen diensten: stel een standaard factuurpauze voor de klant in.',
      },
    ]
  }
  const fouten: FactuurpauzeBevestigVeldfout[] = []
  lijst.forEach((raw, i) => {
    if (leesPauzeAfspraak(raw).facturatieMinuten != null) return
    const type = raw && typeof raw === 'object' ? (raw as Record<string, unknown>).type : undefined
    const label = typeof type === 'string' ? type : `dienst ${i + 1}`
    fouten.push({
      field: `diensten.${i}.pauzeFacturatieMinuten`,
      message: `Kies de factuurpauze voor ${label} (0 min mag: dan gaat er geen pauze van de factuur af).`,
    })
  })
  return fouten
}

/** Afspraak -> de dienst-velden van `PauzeContext`. */
export function dienstAfspraakContext(a: PauzeAfspraak | null | undefined): Pick<
  PauzeContext,
  'dienstPauzeMinuten' | 'dienstVerloning' | 'dienstFacturatieMinuten'
> {
  return {
    dienstPauzeMinuten: a?.minuten ?? null,
    dienstVerloning: a?.verloning ?? null,
    dienstFacturatieMinuten: a?.facturatieMinuten ?? null,
  }
}

/** Afspraak -> de uitzondering-velden van `PauzeContext`. */
export function uitzonderingAfspraakContext(a: PauzeAfspraak | null | undefined): Pick<
  PauzeContext,
  'uitzonderingPauzeMinuten' | 'uitzonderingVerloning' | 'uitzonderingFacturatieMinuten'
> {
  return {
    uitzonderingPauzeMinuten: a?.minuten ?? null,
    uitzonderingVerloning: a?.verloning ?? null,
    uitzonderingFacturatieMinuten: a?.facturatieMinuten ?? null,
  }
}

// ─── Weergave: de opbouw van het urengetal ──────────────────────────────────

function toMinutenVanMiddernacht(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  const h = parseInt(m[1]!, 10)
  const min = parseInt(m[2]!, 10)
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h * 60 + min
}

/**
 * Bruto duur (span) tussen twee `HH:mm`-tijden in minuten, met nacht-overgang
 * (eind < start telt door naar de volgende dag). `null` bij ongeldige invoer.
 */
export function dienstduurMinuten(
  startHHmm: string | null | undefined,
  eindHHmm: string | null | undefined,
): number | null {
  if (!startHHmm || !eindHHmm) return null
  const s = toMinutenVanMiddernacht(startHHmm)
  const e = toMinutenVanMiddernacht(eindHHmm)
  if (s == null || e == null) return null
  let diff = e - s
  if (diff < 0) diff += 24 * 60
  return diff
}

/**
 * Loopt dit tijdvak over middernacht (eind vóór start)? Een dienst die over
 * middernacht loopt is een nachtdienst, ook op een dagdienst-bon.
 */
export function isOverMiddernacht(
  startHHmm: string | null | undefined,
  eindHHmm: string | null | undefined,
): boolean {
  if (!startHHmm || !eindHHmm) return false
  const s = toMinutenVanMiddernacht(startHHmm)
  const e = toMinutenVanMiddernacht(eindHHmm)
  return s != null && e != null && e < s
}

function isPloegSoort(v: unknown): v is PauzePloegSoort {
  return v === 'DAG' || v === 'VROEG' || v === 'LAAT' || v === 'NACHT'
}

/**
 * De ploeg uit een planning-snapshot (`planningJson.dienstTijden`: per ploeg
 * `{ start, eind }`) met exact deze tijden. Twee ploegen met dezelfde tijden
 * zijn niet te onderscheiden: dan `null` (de volgende bron beslist).
 */
export function ploegUitPlanningSnapshot(
  snapshot: unknown,
  startHHmm: string,
  eindHHmm: string,
): PauzePloegSoort | null {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return null
  const dienstTijden = (snapshot as Record<string, unknown>).dienstTijden
  if (!dienstTijden || typeof dienstTijden !== 'object' || Array.isArray(dienstTijden)) return null
  const treffers: PauzePloegSoort[] = []
  for (const [ploeg, tijden] of Object.entries(dienstTijden as Record<string, unknown>)) {
    if (!isPloegSoort(ploeg) || !tijden || typeof tijden !== 'object') continue
    const t = tijden as Record<string, unknown>
    if (t.start === startHHmm && t.eind === eindHHmm) treffers.push(ploeg)
  }
  return treffers.length === 1 ? treffers[0]! : null
}

/** Minuten als `H:mm` ("0:30", "1:15"). Negatief/ongeldig wordt "0:00". */
export function formatPauzeMinuten(minuten: number | null | undefined): string {
  if (!bruikbaar(minuten)) return '0:00'
  const totaal = Math.round(minuten)
  const h = Math.floor(totaal / 60)
  const m = totaal % 60
  return `${h}:${m.toString().padStart(2, '0')}`
}

/** Decimale uren in NL-notatie met komma ("4,75"). */
export function formatUrenNl(uren: number | null | undefined, decimalen = 2): string {
  const n = typeof uren === 'number' && Number.isFinite(uren) ? uren : 0
  return n.toFixed(decimalen).replace('.', ',')
}

/**
 * Uren kort met komma: minstens één decimaal, hooguit twee ("8,0", "7,5",
 * "8,25"). De notatie uit de spec ("betaald 8,0 u · factuur 7,5 u").
 */
export function formatUrenKort(uren: number | null | undefined): string {
  const n = typeof uren === 'number' && Number.isFinite(uren) ? Math.round(uren * 100) / 100 : 0
  let s = n.toFixed(2)
  if (s.endsWith('0')) s = s.slice(0, -1)
  return s.replace('.', ',')
}

export interface UrenOpbouwInput {
  start: string | null | undefined
  eind: string | null | undefined
  pauzeMinutes: number | null | undefined
  uren: number | null | undefined
  /**
   * FO-473 - bij DOORBETAALD gaat de pauze niet van de uren af; de opbouw
   * zegt dat dan ook ("(0:30 pauze doorbetaald)"), anders klopt de som niet.
   */
  pauzeVerloning?: PauzeVerloning | null
}

/**
 * FO-423 §3.3 - de zichtbare opbouw van een dagregel:
 *
 *   "17:45-23:00 - 0:30 pauze = 4,75 uur"
 *   "22:00-06:00 (0:30 pauze doorbetaald) = 8,00 uur"
 *   "08:00-17:00 - geen pauze = 9,00 uur"   (pauze 0: valt meteen op)
 *
 * Eén formatter voor portal, backoffice, werkbon-PDF én de facturatie-
 * specificatie, zodat een ontbrekende pauze overal even zichtbaar is en de
 * weergaven nooit uit elkaar lopen. Zonder bruikbare tijden valt hij terug op
 * alleen het urengetal.
 */
export function formatUrenOpbouw(input: UrenOpbouwInput): string {
  const uren = formatUrenNl(input.uren)
  if (!input.start || !input.eind) return `${uren} uur`
  return `${input.start}-${input.eind} ${formatPauzeSuffix(input.pauzeMinutes, input.pauzeVerloning)} = ${uren} uur`
}

/** Opties van `formatPauzeSuffix`. */
export interface PauzeSuffixOpties {
  /**
   * FO-511 - is de pauze een BEVESTIGDE regel? Default `true` (de verloningsas
   * en interne weergaven: de pauze is daar een feit). Voor de factuurpauze
   * richting de klant geeft de aanroeper hem expliciet mee: zonder bevestigde
   * klantregel wordt een 0 nooit "geen pauze" maar een lege suffix - de klant
   * krijgt geen aanname als feit gepresenteerd.
   */
  bevestigd?: boolean
}

/** Compacte variant zonder urentotaal, voor smalle kolommen (PDF/mobiel). */
export function formatPauzeSuffix(
  pauzeMinutes: number | null | undefined,
  pauzeVerloning?: PauzeVerloning | null,
  opties: PauzeSuffixOpties = {},
): string {
  const pauze = bruikbaar(pauzeMinutes) ? pauzeMinutes : 0
  if (pauze <= 0) return opties.bevestigd === false ? '' : '- geen pauze'
  return pauzeVerloning === 'DOORBETAALD'
    ? `(${formatPauzeMinuten(pauze)} pauze doorbetaald)`
    : `- ${formatPauzeMinuten(pauze)} pauze`
}

/**
 * FO-511 - "pauze afwijkend: 0:15, reden ..." voor een factuurpauze die kantoor
 * afwijkend van de klantregel zette (intern én op de klantbon, spec §5.5).
 * `null` zonder handmatige factuurpauze.
 */
export function formatFactuurpauzeAfwijking(input: {
  pauzeHandmatigFacturatie: boolean
  pauzeFacturatieMinuten: number | null
  pauzeFacturatieReden?: string | null
}): string | null {
  if (!input.pauzeHandmatigFacturatie || input.pauzeFacturatieMinuten == null) return null
  const reden = input.pauzeFacturatieReden?.trim()
  return `pauze afwijkend: ${formatPauzeMinuten(input.pauzeFacturatieMinuten)}${reden ? `, reden: ${reden}` : ''}`
}

/** Liggen betaalde en facturabele uren (op 2 decimalen) uit elkaar? */
export function urenAssenVerschillen(betaald: number, factuur: number): boolean {
  return Math.abs(Math.round(betaald * 100) - Math.round(factuur * 100)) >= 1
}

/**
 * "betaald 8,0 u · factuur 7,5 u" - alleen wanneer de assen verschillen,
 * anders `null` (dan is één urengetal genoeg en voegt de regel niets toe).
 */
export function formatUrenAssen(betaald: number, factuur: number): string | null {
  if (!urenAssenVerschillen(betaald, factuur)) return null
  return `betaald ${formatUrenKort(betaald)} u · factuur ${formatUrenKort(factuur)} u`
}

/** Duur in hele of halve uren voor de werkschema-uitleg ("8 u", "8,5 u"). */
export function formatDienstduur(minuten: number): string {
  const uren = Math.round((minuten / 60) * 100) / 100
  return `${Number.isInteger(uren) ? String(uren) : formatUrenKort(uren)} u`
}

/**
 * De uitkomst van een dienst-afspraak in tekst (spec §D12):
 * "8 u dienst: betaald 8,0 u, factuur 7,5 u".
 */
export function pauzeUitkomstTekst(input: {
  dienstduurMinuten: number | null | undefined
  minuten: number
  verloning: PauzeVerloning
  facturatieMinuten: number
}): string | null {
  const duur = input.dienstduurMinuten
  if (!bruikbaar(duur) || duur <= 0) return null
  const aftrekBetaald = input.verloning === 'DOORBETAALD' ? 0 : input.minuten
  const betaald = Math.max(0, duur - aftrekBetaald) / 60
  const factuur = Math.max(0, duur - input.facturatieMinuten) / 60
  return `${formatDienstduur(duur)} dienst: betaald ${formatUrenKort(betaald)} u, factuur ${formatUrenKort(factuur)} u`
}

/** Leesbare pauze-waarde in het werkschema ("0 min - geen pauze", "30 min"). */
export function formatPauzeKeuze(minuten: number | null | undefined): string {
  if (!bruikbaar(minuten)) return 'niet ingevuld'
  return minuten === 0 ? '0 min - geen pauze' : `${minuten} min`
}

export interface PauzeUitlegInput {
  pauzeMinutes: number
  pauzeVerloning: PauzeVerloning | null
  pauzeFacturatieMinuten: number | null
  pauzeBron: PauzeBron | null
  pauzeVerloningBron: PauzeBron | null
  pauzeFacturatieBron: PauzeBron | null
}

function bronLabel(bron: PauzeBron | null): string {
  return bron ? PAUZE_BRON_LABELS[bron] : 'herkomst niet vastgelegd'
}

/**
 * Uitleg per as voor tooltips en de werkbon-/weekstaatregel, zodat een planner
 * in één oogopslag ziet waarom betaald en gefactureerd verschillen.
 */
export function pauzeUitleg(input: PauzeUitlegInput): { verloning: string; facturatie: string } {
  const verloning = input.pauzeVerloning ?? 'GESCHREVEN'
  const pauzeDeel =
    input.pauzeMinutes > 0
      ? `${formatPauzeMinuten(input.pauzeMinutes)} pauze (${bronLabel(input.pauzeBron)}), ${PAUZE_VERLONING_KORT[verloning]} (${bronLabel(input.pauzeVerloningBron)})`
      : `geen pauze (${bronLabel(input.pauzeBron)})`
  const factuurMinuten = input.pauzeFacturatieMinuten ?? afgeleideFacturatieMinuten(input.pauzeMinutes, verloning)
  const factuurBron: PauzeBron | null =
    input.pauzeFacturatieMinuten == null ? 'AFGELEID' : input.pauzeFacturatieBron
  const facturatieDeel =
    factuurMinuten > 0
      ? `${formatPauzeMinuten(factuurMinuten)} pauze op de factuur (${bronLabel(factuurBron)})`
      : `geen pauze op de factuur (${bronLabel(factuurBron)})`
  return { verloning: `Betaald: ${pauzeDeel}`, facturatie: `Factuur: ${facturatieDeel}` }
}

// ─── Beheer-guard op het klant-werkschema ───────────────────────────────────

/** Minimale dienst-vorm zoals hij in `ClientWorkSchedule.diensten` staat. */
export interface WerkschemaDienstPauzeBron {
  type?: string
  startTime?: string
  endTime?: string
  breakMinutes?: number | null
  pauzeVerloning?: PauzeVerloning | null
  pauzeFacturatieMinuten?: number | null
}

/** Status van een werkschema in het klantoverzicht "pauze-instellingen" (§D13). */
export type WerkschemaPauzeStatusCode = 'BEVESTIGD' | 'AFGELEID' | 'ONVOLLEDIG'

export const WERKSCHEMA_PAUZE_STATUS_LABELS: Record<WerkschemaPauzeStatusCode, string> = {
  BEVESTIGD: 'Bevestigd',
  AFGELEID: 'Afgeleid van de standaard',
  // Eigen afspraken, maar (nog) niet bevestigd - ook als alles is ingevuld.
  ONVOLLEDIG: 'Nog niet bevestigd',
}

export interface WerkschemaDienstUitkomst {
  label: string
  afspraak: PauzeAfspraak
  /** Uitkomst met de defaults ingevuld (werkdatum-onafhankelijk). */
  resolutie: PauzeResolutie
  /** "8 u dienst: betaald 8,0 u, factuur 7,5 u"; `null` zonder bruikbare tijden. */
  uitkomst: string | null
  /** Factuurpauze groter dan de feitelijke pauze (toegestaan, met melding). */
  factuurpauzeBovenPauze: boolean
  /**
   * FO-511 - deze dienst heeft geen eigen factuurpauze én het schema geen
   * klant-standaard: zijn dagregels krijgen de systeemstandaard (onbevestigd).
   */
  factuurpauzeOntbreekt: boolean
  /**
   * FO-511 - de factuurpauze die het beheer voorstelt: de eigen afspraak, anders
   * de klant-standaard, anders de oude schema-brede facturatie-pauze, anders de
   * systeemstandaard per diensttype (korte dienst 0). Bevestigen neemt hem over.
   */
  factuurpauzeVoorstel: number
}

export interface WerkschemaPauzeStatus {
  /** Diensten waarvan `breakMinutes` niet is ingevuld (de standaard geldt). */
  dienstenZonderPauze: string[]
  /**
   * Diensten met pauze 0. Vóór FO-423 schreef het schema een niet-ingevulde
   * pauze weg ALS 0 (`.default(0)`); de FO-473-migratie maakte van elke
   * onbevestigde 0 een `null`. Een 0 is nu een bewuste keuze.
   */
  dienstenNulPauze: string[]
  /** Diensten met een expliciete pauze > 0. */
  dienstenMetPauze: string[]
  /** Per dienst de uitkomst op beide assen. */
  diensten: WerkschemaDienstUitkomst[]
  /** Diensten waarvan de factuurpauze groter is dan de pauze (melding, geen fout). */
  dienstenFactuurpauzeBovenPauze: string[]
  /**
   * Er staat nog een open pauze-vraag op dit schema: niet bevestigd, en
   * minstens één dienst zonder eigen pauze of met pauze 0.
   */
  heeftOntbrekendePauze: boolean
  /**
   * Dubbeltel-waarschuwing (FO-473): er gaat op minstens één dienst meer pauze
   * van de factuur af dan er feitelijk gehouden wordt, of er staat nog een
   * schema-brede facturatie-pauze van vóór FO-473 die NIET is omgezet naar
   * factuurpauze-afspraken. Waarschuwing, geen blokkade.
   */
  dubbeltelRisico: boolean
  /** De schema-brede facturatie-pauze van vóór FO-473 (0 = geen). */
  oudeFacturatiePauzeMinuten: number
  /**
   * Is die oude waarde al omgezet naar factuurpauze-afspraken? Dan telt hij
   * alleen nog als vangnet voor dagregels zonder eigen factuurpauze (zie
   * `oudeFacturatiePauzeIsOmgezet`) en is hij geen dubbeltel-risico.
   */
  oudeFacturatiePauzeOmgezet: boolean
  /** Heeft het schema eigen pauze-afspraken (dienst of klant-default)? */
  heeftEigenAfspraken: boolean
  /**
   * FO-511 - diensten zonder eigen factuurpauze terwijl het schema ook geen
   * klant-standaard heeft. Zolang deze lijst niet leeg is, kan het schema niet
   * bevestigd worden (de factuurpauze is per dienst verplicht).
   */
  dienstenZonderFactuurpauze: string[]
  /** FO-511 - heeft elke dienst een factuurpauze (eigen of via de klant-standaard)? */
  factuurpauzeCompleet: boolean
  status: WerkschemaPauzeStatusCode
}

function leesDiensten(diensten: unknown): WerkschemaDienstPauzeBron[] {
  if (!Array.isArray(diensten)) return []
  const out: WerkschemaDienstPauzeBron[] = []
  for (const raw of diensten) {
    if (!raw || typeof raw !== 'object') continue
    const d = raw as Record<string, unknown>
    const afspraak = leesPauzeAfspraak(d)
    out.push({
      type: typeof d.type === 'string' ? d.type : undefined,
      startTime: typeof d.startTime === 'string' ? d.startTime : undefined,
      endTime: typeof d.endTime === 'string' ? d.endTime : undefined,
      breakMinutes: afspraak.minuten,
      pauzeVerloning: afspraak.verloning,
      pauzeFacturatieMinuten: afspraak.facturatieMinuten,
    })
  }
  return out
}

/**
 * FO-473 - is de oude schema-brede facturatie-pauze omgezet naar
 * factuurpauze-afspraken (per dienst of als klant-default)?
 *
 * De migratie zet de oude waarde om, maar laat hem STAAN: tijdens het
 * deploy-venster leest de vorige revisie hem nog (anders factureert die zonder
 * factuurpauze), en daarna is hij het vangnet voor een dagregel zonder eigen
 * factuurpauze (`facturabeleUren`). Nieuwe dagregels lezen hem niet: naast
 * omgezette afspraken gaat dezelfde pauze dus niet twee keer van de factuur.
 */
export function oudeFacturatiePauzeIsOmgezet(schema: {
  diensten?: unknown
  standaardPauzeFacturatieMinuten?: number | null
}): boolean {
  return (
    bruikbaar(schema.standaardPauzeFacturatieMinuten) ||
    leesDiensten(schema.diensten).some((d) => bruikbaar(d.pauzeFacturatieMinuten))
  )
}

/**
 * FO-473 - een vergelijkbare sleutel van de pauze-AFSPRAKEN van een werkschema:
 * per dienst (type + de drie assen + of het een korte dienst is), per
 * datum-uitzondering, de klant-default, het ploegsysteem (dag of ploeg bepaalt
 * de standaard) en de ingangsdatum van de factuurpauze. Andere tijdwijzigingen
 * tellen niet mee.
 *
 * Gelijke sleutel = gelijke afspraken. Het beheer gebruikt hem om een
 * bevestiging alleen te vernieuwen of te laten vervallen bij een ECHTE
 * wijziging, niet bij elke opslag van het werkschema.
 */
export function pauzeAfsprakenSleutel(schema: {
  diensten?: unknown
  dienstUitzonderingen?: unknown
  standaardPauzeMinuten?: number | null
  standaardPauzeVerloning?: PauzeVerloning | null
  standaardPauzeFacturatieMinuten?: number | null
  facturatiePauzeVanaf?: Date | string | null
  shiftType?: string | null
}): string {
  const dag = (v: Date | string | null | undefined): string | null => {
    if (v == null || v === '') return null
    const d = v instanceof Date ? v : new Date(v)
    return Number.isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10)
  }
  const uitzonderingen = Array.isArray(schema.dienstUitzonderingen)
    ? (schema.dienstUitzonderingen as unknown[]).map((u) => {
        const r = u && typeof u === 'object' ? (u as Record<string, unknown>) : {}
        const a = leesPauzeAfspraak(r)
        return [typeof r.datum === 'string' ? r.datum : null, a.minuten, a.verloning, a.facturatieMinuten]
      })
    : []
  const korteDienst = (d: WerkschemaDienstPauzeBron): boolean | null => {
    if (!d.startTime || !d.endTime) return null
    const duur = dienstduurMinuten(d.startTime, d.endTime)
    return duur != null && duur > 0 && duur < KORTE_DIENST_DREMPEL_MINUTEN
  }
  return JSON.stringify({
    s: schema.shiftType ?? null,
    d: leesDiensten(schema.diensten).map((d) => [
      d.type ?? null,
      d.breakMinutes ?? null,
      d.pauzeVerloning ?? null,
      d.pauzeFacturatieMinuten ?? null,
      korteDienst(d),
    ]),
    u: uitzonderingen,
    k: [
      bruikbaar(schema.standaardPauzeMinuten) ? schema.standaardPauzeMinuten : null,
      isPauzeVerloning(schema.standaardPauzeVerloning) ? schema.standaardPauzeVerloning : null,
      bruikbaar(schema.standaardPauzeFacturatieMinuten) ? schema.standaardPauzeFacturatieMinuten : null,
    ],
    v: dag(schema.facturatiePauzeVanaf),
  })
}

/** ShiftType van het werkschema -> diensttype voor de systeemdefault. */
function dienstTypeVoorShiftType(shiftType: string | null | undefined): PauzeDienstType | null {
  switch (shiftType) {
    case 'DAG':
      return 'DAGDIENST'
    case 'PLOEGEN_2':
    case 'PLOEGEN_3':
    case 'PLOEGEN_4':
    case 'PLOEGEN_5':
      return shiftType
    default:
      return null
  }
}

function ploegSoortVan(type: string | undefined): PauzePloegSoort | null {
  return type === 'DAG' || type === 'VROEG' || type === 'LAAT' || type === 'NACHT' ? type : null
}

/**
 * Beoordeelt de pauze-configuratie van één klant-werkschema (FO-423 §3.4 +
 * FO-473 §D): welke diensten hebben geen eigen pauze, wat is per dienst de
 * uitkomst op beide assen, en wat is de status voor het klantoverzicht?
 *
 * Puur, zodat het beheerscherm, de API-guard, het klantoverzicht en de
 * herreken-run exact dezelfde beoordeling gebruiken.
 */
export function beoordeelWerkschemaPauze(schema: {
  diensten: unknown
  /** `ClientWorkSchedule.shiftType` - bepaalt de systeemdefault per dienst. */
  shiftType?: string | null
  /**
   * Schema-brede facturatie-pauze van vóór FO-473. Na de migratie omgezet
   * naar afspraken per dienst/klant, maar blijft staan als vangnet.
   */
  facturatiePauzeMinuten?: number | null
  /**
   * Is de pauze van dit schema bewust vastgelegd (`pauzeBevestigdOp`)? Zolang
   * dat niet zo is, staat het schema op de werklijst.
   */
  pauzeBevestigd?: boolean
  standaardPauzeMinuten?: number | null
  standaardPauzeVerloning?: PauzeVerloning | null
  standaardPauzeFacturatieMinuten?: number | null
  systeemDefaults?: Partial<PauzeSysteemDefaults> | null
}): WerkschemaPauzeStatus {
  const diensten = leesDiensten(schema.diensten)
  const zonder: string[] = []
  const nul: string[] = []
  const met: string[] = []
  const bovenPauze: string[] = []
  const uitkomsten: WerkschemaDienstUitkomst[] = []
  const zonderFactuurpauze: string[] = []
  const dienstType = dienstTypeVoorShiftType(schema.shiftType)
  const oudeFacturatiePauze = Number(schema.facturatiePauzeMinuten ?? 0)
  const oudeFacturatiePauzeMinuten =
    Number.isFinite(oudeFacturatiePauze) && oudeFacturatiePauze > 0 ? oudeFacturatiePauze : 0
  const klantFactuurpauze = bruikbaar(schema.standaardPauzeFacturatieMinuten)
    ? schema.standaardPauzeFacturatieMinuten
    : null

  for (let i = 0; i < diensten.length; i++) {
    const d = diensten[i]!
    const label = d.type ?? `dienst ${i + 1}`
    if (!bruikbaar(d.breakMinutes)) zonder.push(label)
    else if (d.breakMinutes > 0) met.push(label)
    else nul.push(label)

    const afspraak: PauzeAfspraak = {
      minuten: d.breakMinutes ?? null,
      verloning: d.pauzeVerloning ?? null,
      facturatieMinuten: d.pauzeFacturatieMinuten ?? null,
    }
    const duur = dienstduurMinuten(d.startTime, d.endTime)
    const resolutie = resolvePauze({
      ...dienstAfspraakContext(afspraak),
      klantDefaultPauzeMinuten: schema.standaardPauzeMinuten ?? null,
      klantDefaultVerloning: schema.standaardPauzeVerloning ?? null,
      klantDefaultFacturatieMinuten: schema.standaardPauzeFacturatieMinuten ?? null,
      dienstType,
      ploegSoort: ploegSoortVan(d.type),
      dienstduurMinuten: duur,
      systeemDefaults: schema.systeemDefaults ?? null,
    })
    const boven = factuurpauzeBovenPauze(resolutie)
    if (boven) bovenPauze.push(label)
    // FO-511 - de factuurpauze is per dienst verplicht (of via de klant-standaard).
    const factuurpauzeOntbreekt = afspraak.facturatieMinuten == null && klantFactuurpauze == null
    if (factuurpauzeOntbreekt) zonderFactuurpauze.push(label)
    const dienst: FacturatieDienst = { dienstType, ploegSoort: ploegSoortVan(d.type), dienstduurMinuten: duur }
    const factuurpauzeVoorstel =
      afspraak.facturatieMinuten ??
      klantFactuurpauze ??
      klemPauze(
        oudeFacturatiePauzeMinuten > 0
          ? oudeFacturatiePauzeMinuten
          : facturatieSysteemVoorstel(dienst, schema.systeemDefaults ?? null),
        FACTUURPAUZE_MAX_MINUTEN,
        bruikbareDuur(duur),
      )
    uitkomsten.push({
      label,
      afspraak,
      resolutie,
      uitkomst: pauzeUitkomstTekst({
        dienstduurMinuten: duur,
        minuten: resolutie.minuten,
        verloning: resolutie.verloning,
        facturatieMinuten: resolutie.facturatieMinuten,
      }),
      factuurpauzeBovenPauze: boven,
      factuurpauzeOntbreekt,
      factuurpauzeVoorstel,
    })
  }

  const oudeFacturatiePauzeOmgezet =
    oudeFacturatiePauzeMinuten > 0 && oudeFacturatiePauzeIsOmgezet(schema)
  const heeftEigenAfspraken =
    diensten.some(
      (d) => d.breakMinutes != null || d.pauzeVerloning != null || d.pauzeFacturatieMinuten != null,
    ) ||
    schema.standaardPauzeMinuten != null ||
    schema.standaardPauzeVerloning != null ||
    schema.standaardPauzeFacturatieMinuten != null
  const bevestigd = schema.pauzeBevestigd === true
  // Zonder diensten is er alleen de klant-standaard om op terug te vallen.
  const factuurpauzeCompleet =
    klantFactuurpauze != null || (diensten.length > 0 && zonderFactuurpauze.length === 0)

  return {
    dienstenZonderPauze: zonder,
    dienstenNulPauze: nul,
    dienstenMetPauze: met,
    diensten: uitkomsten,
    dienstenFactuurpauzeBovenPauze: bovenPauze,
    heeftOntbrekendePauze: !bevestigd && (zonder.length > 0 || nul.length > 0),
    dubbeltelRisico:
      bovenPauze.length > 0 || (oudeFacturatiePauzeMinuten > 0 && !oudeFacturatiePauzeOmgezet),
    oudeFacturatiePauzeMinuten,
    oudeFacturatiePauzeOmgezet,
    heeftEigenAfspraken,
    dienstenZonderFactuurpauze: zonderFactuurpauze,
    factuurpauzeCompleet,
    // FO-511 - bevestigd telt pas als elke dienst ook een factuurpauze heeft:
    // anders krijgen zijn dagregels de (onbevestigde) systeemstandaard.
    status:
      bevestigd && factuurpauzeCompleet
        ? 'BEVESTIGD'
        : heeftEigenAfspraken || bevestigd
          ? 'ONVOLLEDIG'
          : 'AFGELEID',
  }
}

/** Een dienst zoals klant-invoer (onboarding) hem aanlevert: alleen de feitelijke pauze. */
export interface KlantDienstInvoer {
  type: string
  startTime: string
  endTime: string
  breakMinutes: number | null
}

/** Een dienst na `behoudInternePauzeAssen`: de klant-velden plus de interne assen van het rooster. */
export interface KlantDienstMetInterneAssen {
  type: string
  startTime: string
  endTime: string
  breakMinutes: number | null
  pauzeVerloning?: PauzeVerloning
  pauzeFacturatieMinuten?: number
}

export interface InternePauzeAssenResultaat {
  diensten: KlantDienstMetInterneAssen[]
  /**
   * Wijkt de pauze of de dienst-indeling af van wat er stond? Dan hoort de
   * bevestiging van de pauze-afspraak eraf: HR kijkt de afspraak opnieuw na.
   */
  pauzeGewijzigd: boolean
}

/**
 * FO-473 - haalt de interne pauze-assen (verloning, factuurpauze) uit
 * klant-invoer. Een klant mag die nooit zetten, ook niet door ze extra in de
 * payload mee te sturen (`.passthrough()`-schema's).
 */
export function zonderInternePauzeAssen<T extends object>(
  dienst: T,
): Omit<T, 'pauzeVerloning' | 'pauzeFacturatieMinuten'> {
  const kopie: Record<string, unknown> = { ...(dienst as Record<string, unknown>) }
  delete kopie.pauzeVerloning
  delete kopie.pauzeFacturatieMinuten
  return kopie as Omit<T, 'pauzeVerloning' | 'pauzeFacturatieMinuten'>
}

/**
 * FO-473 - klant-invoer (de onboarding-wizard) kent alleen de feitelijke pauze;
 * verloning en factuurpauze zijn interne afspraken. Werkt de klant een
 * bestaand rooster bij, dan blijven die interne assen per dienst staan
 * (koppeling op type + starttijd, anders op type) i.p.v. stil te verdwijnen -
 * dat zou de factuur of de verloning ongemerkt veranderen.
 *
 * De uitkomst bevat UITSLUITEND de klant-velden (type, tijden, pauze) plus de
 * interne assen van het bestaande rooster: wat de klant verder meestuurt
 * (ook een eigen `pauzeFacturatieMinuten`) valt weg. Een nieuw rooster
 * (`bestaandRaw` leeg) krijgt dus geen interne assen.
 */
export function behoudInternePauzeAssen(
  nieuw: ReadonlyArray<KlantDienstInvoer>,
  bestaandRaw: unknown,
): InternePauzeAssenResultaat {
  const bestaand = leesDiensten(bestaandRaw)
  const gebruikt = new Set<number>()
  let pauzeGewijzigd = nieuw.length !== bestaand.length
  const diensten = nieuw.map((d) => {
    let idx = bestaand.findIndex(
      (b, i) => !gebruikt.has(i) && b.type === d.type && b.startTime === d.startTime,
    )
    if (idx < 0) idx = bestaand.findIndex((b, i) => !gebruikt.has(i) && b.type === d.type)
    // Alleen de klant-velden; een ongeldige pauze telt als "niet ingevuld".
    const klantDeel: KlantDienstMetInterneAssen = {
      type: d.type,
      startTime: d.startTime,
      endTime: d.endTime,
      breakMinutes: bruikbaar(d.breakMinutes) ? d.breakMinutes : null,
    }
    if (idx < 0) {
      pauzeGewijzigd = true
      return klantDeel
    }
    gebruikt.add(idx)
    const oud = bestaand[idx]!
    if ((oud.breakMinutes ?? null) !== klantDeel.breakMinutes) pauzeGewijzigd = true
    return {
      ...klantDeel,
      ...(oud.pauzeVerloning != null ? { pauzeVerloning: oud.pauzeVerloning } : {}),
      ...(oud.pauzeFacturatieMinuten != null
        ? { pauzeFacturatieMinuten: oud.pauzeFacturatieMinuten }
        : {}),
    }
  })
  return { diensten, pauzeGewijzigd }
}

/**
 * De diensten waarvoor nog een pauze-keuze open staat: niet ingevuld, plus de
 * 0-pauzes zolang het schema niet bevestigd is. Gedeeld door de badge, de
 * werklijst en het herstel-rapport zodat ze exact dezelfde diensten noemen.
 */
export function openPauzeVragen(status: WerkschemaPauzeStatus, pauzeBevestigd?: boolean): string[] {
  return pauzeBevestigd === true
    ? []
    : [...status.dienstenZonderPauze, ...status.dienstenNulPauze]
}

/**
 * Menselijke uitleg bij `dubbeltelRisico` - gebruikt in het werkschema-beheer,
 * de API-waarschuwing en het herstel-rapport, zodat ze exact hetzelfde zeggen.
 */
export function pauzeDubbeltelMelding(
  status: Pick<WerkschemaPauzeStatus, 'dienstenFactuurpauzeBovenPauze' | 'oudeFacturatiePauzeMinuten'> &
    Partial<Pick<WerkschemaPauzeStatus, 'oudeFacturatiePauzeOmgezet'>>,
): string {
  const delen: string[] = []
  if (status.dienstenFactuurpauzeBovenPauze.length > 0) {
    delen.push(
      `Bij ${status.dienstenFactuurpauzeBovenPauze.join(', ')} gaat er meer pauze van de factuur af dan ` +
        'er feitelijk gehouden wordt. Dat mag (een contractuele factuurpauze), maar controleer dat ' +
        'dezelfde pauze niet twee keer van de factuur gaat.',
    )
  }
  if (status.oudeFacturatiePauzeMinuten > 0 && status.oudeFacturatiePauzeOmgezet !== true) {
    delen.push(
      `Dit werkschema heeft nog een schema-brede facturatie-pauze van ${status.oudeFacturatiePauzeMinuten} ` +
        'minuten van vóór de tweeassige pauze. Nieuwe werkdagen gebruiken die niet meer: zet hem om naar ' +
        'de factuurpauze per dienst of als standaard van de klant, anders gaat hij niet meer van de factuur af.',
    )
  }
  return delen.join(' ')
}
